/**
 * Billing & Invoices: Accounting nav group
 * - Billing tab (RsPageBillingReports.vue)
 * - Invoices tab (Invoices.vue + RsModalReviewInvoices.vue)
 *
 * Generic UI strings (Cities, Properties, Status, OK, Export CSV, N/A, No Results Found,
 * the "Remove (N) Filter" button) are intentionally reused from `globals`/`dataTableFields`
 * rather than duplicated here.
 */
export const billingEn = {
  billing: {
    navigation: {
      billing: 'Billing',
      invoices: 'Invoices',
      accountingOverview: 'Accounting Overview',
    },
    // Invoice status display labels. NOTE: the underlying status values
    // ('paid' | 'unpaid' | 'overdue') are logic keys and stay hard-coded in code.
    statuses: {
      paid: 'Paid',
      unpaid: 'Unpaid',
      overdue: 'Overdue',
    },
    invoices: {
      filters: {
        invoiceNumber: 'Invoice Number',
        invoiceStatus: 'Invoice Status',
        ownershipGroups: 'Ownership Groups',
        invoiceDate: 'Invoice Date',
        allPlaceholder: '[All]',
      },
      selectAllTooltip: 'Check this box on each page to select all unpaid and overdue invoices on the respective page.',
      columns: {
        invoiceNumber: 'Invoice #',
        description: 'Description',
        ownershipGroups: 'Ownership Groups',
        invoiceDate: 'Invoice Date',
        invoiceDueDate: 'Invoice Due Date',
        totalAmount: 'Total Amount',
        outstandingAmount: 'Outstanding Amount',
        actions: 'Actions',
      },
      blank: '(blank)',
      viewBankingInformation: 'View Banking Information',
      totalOutstandingAmount: 'Total Outstanding Amount',
      selected: 'Selected: {count}',
      bulkPay: 'Bulk Pay Invoices',
      pay: 'Pay',
      refresh: 'Refresh',
      downloadInvoice: 'Download Invoice',
      paymentCompleted: 'Payment has been completed successfully.',
      noPaymentPermission: 'You do not have the permission to make payments.',
      refreshModal: {
        title: 'Refresh Page After Payment',
        messageStart: 'An update has been made to the page in another tab.',
        messageEmphasis: 'Please complete your payment before refreshing the page to view those changes.',
      },
      popupModal: {
        title: 'Popup Blocked',
        message: 'Popup blocked. Please allow popups for this site.',
      },
      bankingModal: {
        title: 'Banking Information',
        beneficiaryCompanyName: 'Beneficiary Company Name:',
        beneficiaryAddress: 'Beneficiary Address:',
        beneficiaryBankName: 'Beneficiary Bank Name:',
        beneficiaryBankAddress: 'Beneficiary Bank Address:',
        institutionBankCode: 'Institution Bank Code:',
        transit: 'Transit:',
        accountNumberCad: 'Account Number CAD Account:',
        swiftCode: 'SWIFT CODE:',
      },
      review: {
        title: 'Review Invoices',
        bannerMessage: 'Please note that partial payments are not available for bulk payments.',
        invoices: 'Invoices',
        invoicesSelected: 'Invoices Selected ({count})',
        total: 'Total:',
        clickToUndo: 'Click to Undo',
        discard: 'Discard',
      },
    },
    reports: {
      tabs: {
        allServices: 'All Services',
        costByProperty: 'Cost By Property',
        appliedCredits: 'Applied Credits',
      },
      banner: {
        viewingInfo: 'You are seeing billing information for {date}',
        invoicesButton: 'Invoices',
      },
      filters: {
        billingMonth: 'Billing Month',
        serviceType: 'Service Type',
        allPlaceholder: '[All]',
        showWithoutCharges: 'Show properties without charges',
        removeFilter: 'Remove Filter',
        removeFilters: 'Remove {count} Filters',
      },
      totals: {
        subtotal: 'Subtotal',
        taxes: 'Taxes',
        totalOwing: 'Total Owing',
        inclusiveOfTaxes: 'Inclusive of taxes',
      },
      estimateWarning: 'Amounts on this summary are estimates and may differ from the final invoice by a small amount due to differences in how the costs are detailed in this report.',
      serviceTypes: {
        flatRate: 'Flat Rate',
        byUsage: 'By Usage',
        callTracking: 'Call Tracking',
      },
      billingType: {
        byUsage: 'By Usage',
        flatFee: 'Flat Fee',
      },
      callTrackingSection: 'Call Tracking',
      showProperties: 'Show Properties',
      hideProperties: 'Hide Properties',
      exportToCsv: 'Export to CSV',
      tooltips: {
        usage: 'Usage refers to each lead, call or day used',
        maxPerProperty: 'Maximum monthly fee per property',
        taxEstimate: 'Taxes shown here are estimates, final tax numbers will be on the invoice',
      },
      columns: {
        propertiesCount: 'Properties #',
        trackingLines: 'Tracking Lines #',
        minUsed: 'Min. Used',
        minRecorded: 'Min. Recorded',
        smsReceived: 'SMS Recieved',
        totalCalls: 'Total Calls',
        subtotal: 'Subtotal',
        tax: 'Tax',
        total: 'Total',
        citiesCount: 'Cities #',
        costPerUsage: 'Cost Per Usage',
        maxPerProperty: 'Max/Property',
        totalUsage: 'Total Usage',
        monthlyFee: 'Monthly Fee',
        invoiceNumber: 'Invoice Number',
        ownershipGroup: 'Ownership Group',
        address: 'Address',
        usage: 'Usage',
        credit: 'Credit',
        invoice: 'Invoice',
        description: 'Description',
        amount: 'Amount',
        accountingCode: 'Accounting Code',
        building: 'Building',
        taxGst: 'Tax(GST)',
        totalAfterTax: 'Total After Tax',
      },
    },
  },
} as const;
export const billingFr = {
  billing: {
    navigation: {
      billing: 'Facturation',
      invoices: 'Factures',
      accountingOverview: 'Aperçu de la comptabilité',
    },
    statuses: {
      paid: 'Payé',
      unpaid: 'Impayé',
      overdue: 'En retard',
    },
    invoices: {
      filters: {
        invoiceNumber: 'Numéro de facture',
        invoiceStatus: 'Statut de la facture',
        ownershipGroups: 'Groupes de propriété',
        invoiceDate: 'Date de facturation',
        allPlaceholder: '[Tous]',
      },
      selectAllTooltip: 'Cochez cette case sur chaque page pour sélectionner toutes les factures impayées et en retard de la page correspondante.',
      columns: {
        invoiceNumber: 'Nº de facture',
        description: 'Description',
        ownershipGroups: 'Groupes de propriété',
        invoiceDate: 'Date de facturation',
        invoiceDueDate: 'Date d\'échéance',
        totalAmount: 'Montant total',
        outstandingAmount: 'Montant impayé',
        actions: 'Actions',
      },
      blank: '(vide)',
      viewBankingInformation: 'Voir les renseignements bancaires',
      totalOutstandingAmount: 'Montant total impayé',
      selected: 'Sélectionné : {count}',
      bulkPay: 'Payer les factures en lot',
      pay: 'Payer',
      refresh: 'Actualiser',
      downloadInvoice: 'Télécharger la facture',
      paymentCompleted: 'Le paiement a été effectué avec succès.',
      noPaymentPermission: 'Vous n\'avez pas la permission d\'effectuer des paiements.',
      refreshModal: {
        title: 'Actualiser la page après le paiement',
        messageStart: 'Une mise à jour a été effectuée sur la page dans un autre onglet.',
        messageEmphasis: 'Veuillez effectuer votre paiement avant d\'actualiser la page pour voir ces modifications.',
      },
      popupModal: {
        title: 'Fenêtre contextuelle bloquée',
        message: 'Fenêtre contextuelle bloquée. Veuillez autoriser les fenêtres contextuelles pour ce site.',
      },
      bankingModal: {
        title: 'Renseignements bancaires',
        beneficiaryCompanyName: 'Nom de l\'entreprise bénéficiaire :',
        beneficiaryAddress: 'Adresse du bénéficiaire :',
        beneficiaryBankName: 'Nom de la banque bénéficiaire :',
        beneficiaryBankAddress: 'Adresse de la banque bénéficiaire :',
        institutionBankCode: 'Code d\'institution bancaire :',
        transit: 'Transit :',
        accountNumberCad: 'Numéro de compte (compte CAD) :',
        swiftCode: 'Code SWIFT :',
      },
      review: {
        title: 'Examiner les factures',
        bannerMessage: 'Veuillez noter que les paiements partiels ne sont pas disponibles pour les paiements groupés.',
        invoices: 'Factures',
        invoicesSelected: 'Factures sélectionnées ({count})',
        total: 'Total :',
        clickToUndo: 'Cliquer pour annuler',
        discard: 'Rejeter',
      },
    },
    reports: {
      tabs: {
        allServices: 'Tous les services',
        costByProperty: 'Coût par propriété',
        appliedCredits: 'Crédits appliqués',
      },
      banner: {
        viewingInfo: 'Vous consultez les informations de facturation pour {date}',
        invoicesButton: 'Factures',
      },
      filters: {
        billingMonth: 'Mois de facturation',
        serviceType: 'Type de service',
        allPlaceholder: '[Tous]',
        showWithoutCharges: 'Afficher les propriétés sans frais',
        removeFilter: 'Retirer le filtre',
        removeFilters: 'Retirer {count} filtres',
      },
      totals: {
        subtotal: 'Sous-total',
        taxes: 'Taxes',
        totalOwing: 'Total dû',
        inclusiveOfTaxes: 'Taxes comprises',
      },
      estimateWarning: 'Les montants de ce sommaire sont des estimations et peuvent différer légèrement de la facture finale en raison de la façon dont les coûts sont détaillés dans ce rapport.',
      serviceTypes: {
        flatRate: 'Tarif forfaitaire',
        byUsage: 'Selon l\'utilisation',
        callTracking: 'Suivi des appels',
      },
      billingType: {
        byUsage: 'Selon l\'utilisation',
        flatFee: 'Tarif forfaitaire',
      },
      callTrackingSection: 'Suivi des appels',
      showProperties: 'Afficher les propriétés',
      hideProperties: 'Masquer les propriétés',
      exportToCsv: 'Exporter en CSV',
      tooltips: {
        usage: 'L\'utilisation correspond à chaque prospect, appel ou jour utilisé',
        maxPerProperty: 'Frais mensuels maximaux par propriété',
        taxEstimate: 'Les taxes indiquées ici sont des estimations; les montants définitifs figureront sur la facture',
      },
      columns: {
        propertiesCount: 'Nb de propriétés',
        trackingLines: 'Nb de lignes de suivi',
        minUsed: 'Min. utilisées',
        minRecorded: 'Min. enregistrées',
        smsReceived: 'SMS reçus',
        totalCalls: 'Total des appels',
        subtotal: 'Sous-total',
        tax: 'Taxe',
        total: 'Total',
        citiesCount: 'Nb de villes',
        costPerUsage: 'Coût par utilisation',
        maxPerProperty: 'Max/propriété',
        totalUsage: 'Utilisation totale',
        monthlyFee: 'Frais mensuels',
        invoiceNumber: 'Numéro de facture',
        ownershipGroup: 'Groupe de propriété',
        address: 'Adresse',
        usage: 'Utilisation',
        credit: 'Crédit',
        invoice: 'Facture',
        description: 'Description',
        amount: 'Montant',
        accountingCode: 'Code comptable',
        building: 'Immeuble',
        taxGst: 'Taxe (TPS)',
        totalAfterTax: 'Total après taxes',
      },
    },
  },
} as const;
